import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';
import { useEditorState } from '@tiptap/react';
import { FileSymlink } from 'lucide-react';
import { forwardRef, useCallback } from 'react';
import { toast } from 'sonner';

function sectionTitleFromDocAttrs(title: Record<string, string> | null | undefined): string | null {
    if (!title || typeof title !== 'object') {
        return null;
    }
    const defaultTitle = title.default;
    if (typeof defaultTitle === 'string' && defaultTitle.length > 0) {
        return defaultTitle;
    }
    const first = Object.values(title).find(v => typeof v === 'string' && v.length > 0);
    return first ?? null;
}

const CopySectionReferenceButton = forwardRef<HTMLButtonElement, { className?: string }>(function CopySectionReferenceButton(
    { className },
    ref,
) {
    const { editor } = useTiptapEditor();

    const { sectionId, sectionTitle } = useEditorState({
        editor,
        selector: ({ editor: ed }) => {
            if (!ed) {
                return { sectionId: null as string | null, sectionTitle: null as string | null };
            }
            const attrs = ed.state.doc.attrs;
            const rawSectionId = attrs.sectionId;
            const sectionIdValue = typeof rawSectionId === 'string' && rawSectionId.length > 0 ? rawSectionId : null;
            const titleRecord = attrs.title as Record<string, string> | undefined;
            return {
                sectionId: sectionIdValue,
                sectionTitle: sectionTitleFromDocAttrs(titleRecord),
            };
        },
    }) ?? { sectionId: null, sectionTitle: null };

    const handleCopy = useCallback(async () => {
        if (!sectionId) {
            return;
        }
        await navigator.clipboard.writeText(`reference:${sectionId}#`);
        toast.success('Section reference copied', {
            description: sectionTitle ? `Reference to "${sectionTitle}" has been copied to clipboard` : undefined,
        });
    }, [sectionId, sectionTitle]);

    if (!editor) {
        return null;
    }

    return (
        <Button
            ref={ref}
            type="button"
            className={className}
            data-style="ghost"
            aria-label="Copy section reference"
            tooltip="Copy section reference"
            disabled={!sectionId}
            onClick={() => {
                void handleCopy();
            }}
        >
            <FileSymlink className="tiptap-button-icon" />
        </Button>
    );
});

export default CopySectionReferenceButton;
